import {
  Box,
  Button,
  Grid,
  makeStyles,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@material-ui/core";
import { setAutoFreeze } from "immer";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";

import apiHelper from "../../../../util/ApiHelper";
import ImageUpload from "../../../ImageUpload";
import { useLoading } from "../../../../util/LoadingProvider";
import editorConfiguration from "../../../../util/EditorConfiguration";
import { toast } from "react-toastify";
import { KeyboardDateTimePicker } from "@material-ui/pickers";

const frontendUrl = process.env.REACT_APP_CINEFAN_WEB_HOSTNAME;
setAutoFreeze(false);

const useStyles = makeStyles((theme) => ({
  tabpanel: {
    background: "#FFFFFF",
  },
  textInput: {
    backgroundColor: "white",
    width: "100%",
    marginBottom: "12px",

    "& input": {
      fontSize: "14px",
    },
  },
  content: {
    "& .ck-editor__editable_inline": {
      minHeight: "200px",
    },
  },
  buttonGroup: {
    marginTop: "20px",
    textAlign: "right",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  box: {
    padding: 0,
  },
  innerBox: {
    background: "#ffffff",
    padding: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  imageGallery: {
    border: "1px solid #CCCCCC",
    minHeight: "160px",
    borderRadius: "3px",
    position: "relative",
  },
  imageGalleryControl: {
    position: "relative",
    width: "100%",
    display: "flex",
    height: "40px",
    bottom: "0",
    borderTop: "1px solid #CCCCCC",
    padding: "5px",
  },
  imageGalleryBlk: {
    minHeight: "120px",
    padding: "5px",
  },
  imgPreview: {
    maxWidth: "200px",
    margin: "10px",
    display: "inline-flex",
    position: "relative",
    verticalAlign: "top",
    "& img": {
      width: "100%",
      height: "auto",
    },
    "& button": {
      position: "absolute",
      top: "-10px",
      right: "-10px",
      zIndex: "10",
      display: "block",
      color: "rgba(48, 111, 157, 0.8)",
      background: "white",
      padding: 0,
      transition: "200ms all ease-out",
      "&:hover": {
        color: "rgba(48, 111, 157, 1)",
        background: "white",
      },
    },
  },
  permalink: {
    display: "flex",
    alignItems: "center",
    fontSize: "14px",
    color: "#333333",
    padding: "0 5px",
    "& .link": {
      color: "#306F9D",
      margin: "0 10px",
      display: "flex",
      alignItems: "center",
      "& input": {
        padding: "7px 14px",
        fontSize: "13px",
      },
    },
    "& button": {
      padding: "2px 20px",
      textTransform: "inherit",
      color: "#727272",
    },
  },
}));

function TabPanel(props) {
  const classes = useStyles();
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`nav-tabpanel-${index}`}
      aria-labelledby={`nav-tab-${index}`}
      {...other}
    >
      <Box p={3} className={classes.box}>
        {children}
      </Box>
    </div>
  );
}

const defaultFormValues = {
  localizations: {
    en: {
      title: "",
      slug: "",
      content: "",
      image: "",
    },
    tc: {
      title: "",
      slug: "",
      content: "",
      image: "",
    },
  },
  publishDate: null,
};

function a11yProps(index) {
  return {
    id: `nav-tab-${index}`,
    "aria-controls": `nav-tabpanel-${index}`,
  };
}

function AddNewsPage({ disabled, ...props }) {
  const { history } = props;
  const classes = useStyles();
  const [activeTab, setActiveTab] = React.useState(0);
  const [defaultValues] = React.useState(defaultFormValues);

  const { setLoading } = useLoading();

  const {
    control,
    handleSubmit,
    setValue,
  } = useForm({
    defaultValues: React.useMemo(() => defaultValues, [defaultValues]),
  });

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const onSubmit = (articleStatus) => async (value) => {
    try {
      setLoading(true);
      value.status = articleStatus;
      value.category = "news";
      const result = await apiHelper.cinefan.createPage(value);
      if (result.data.id) {
        toast.dark("News updated.");
        if (value.status === "PREVIEW") {
          window.open(
            frontendUrl + "/preview/pages/" + result.data.id,
            "_blank"
          );
        } else {
          history.push(`/cinefan/news/${result.data.id}`);
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.content}>
      <Typography variant="h4" gutterBottom>
        News > Add News
      </Typography>
      <Tabs
        value={activeTab}
        onChange={handleChange}
        aria-label="nav tabs example"
        TabIndicatorProps={{ style: { top: "0", background: "#306F9D" } }}
      >
        <Tab
          label="English"
          {...a11yProps(0)}
        />
        <Tab
          label="繁體中文"
          {...a11yProps(1)}
        />
      </Tabs>
      <form>
        <fieldset disabled={disabled} className="fieldset">
          <TabPanel value={activeTab} index={0}>
            <Box className={classes.innerBox} style={{ marginBottom: 0 }}>
              <Grid container direction="vertical" spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    defaultValue={defaultValues.localizations.en.title}
                    name="localizations.en.title"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label="News Title"
                        variant="outlined"
                        margin="dense"
                        className={classes.textInput}
                        error={!!error}
                        helperText={error ? error.message : ""}
                        {...field}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <div className={classes.permalink}>
                    Permalink:
                    <div className="link">
                      <span>{frontendUrl || "https://cinefan.com.hk"}/</span>
                      <Controller
                        name={`localizations.en.slug`}
                        control={control}
                        defaultValue={
                          defaultValues && defaultValues.localizations.en.slug
                        }
                        render={({
                          field: { value, onChange, ...rest },
                          fieldState: { error },
                        }) => (
                          <TextField
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            {...rest}
                            value={value}
                            onChange={(event) => {
                              onChange(event);
                              setValue(
                                "localizations.tc.slug",
                                event.target.value
                              );
                            }}
                            error={!!error}
                            helperText={error ? "slug required" : ""}
                          />
                        )}
                      />
                    </div>
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <ImageUpload
                    control={control}
                    name="localizations.en.image"
                    label="Image"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="localizations.en.content"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CKEditor
                        editor={Editor}
                        config={editorConfiguration}
                        data={field.value || ""}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          field.onChange(data);
                        }}
                        disabled={disabled}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            <Box className={classes.innerBox} style={{ marginBottom: 0 }}>
              <Grid container direction="vertical" spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    defaultValue={defaultValues.localizations.en.title}
                    name="localizations.tc.title"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label="News Title"
                        variant="outlined"
                        margin="dense"
                        className={classes.textInput}
                        error={!!error}
                        helperText={error ? error.message : ""}
                        {...field}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <div className={classes.permalink}>
                    Permalink:
                    <div className="link">
                      <span>{frontendUrl || "https://cinefan.com.hk"}/zh/</span>
                      <Controller
                        name={`localizations.tc.slug`}
                        control={control}
                        defaultValue={
                          defaultValues && defaultValues.localizations.tc.slug
                        }
                        render={({
                          field: { value, onChange, ...rest },
                          fieldState: { error },
                        }) => (
                          <TextField
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            {...rest}
                            value={value}
                            onChange={(event) => {
                              onChange(event);
                              setValue(
                                "localizations.tc.slug",
                                event.target.value
                              );
                            }}
                            error={!!error}
                            helperText={error ? "slug required" : ""}
                          />
                        )}
                      />
                    </div>
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <ImageUpload
                    control={control}
                    name="localizations.tc.image"
                    label="Image"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="localizations.tc.content"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CKEditor
                        editor={Editor}
                        config={editorConfiguration}
                        data={field.value || ""}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          field.onChange(data);
                        }}
                        disabled={disabled}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          </TabPanel>

          <Box className={classes.innerBox} style={{ marginTop: "20px" }}>
            <Grid container xs={12} direction="column" spacing={3}>
              <Grid container item xs={12}>
                <Typography variant="span">Publish Settings</Typography>
              </Grid>
              <Grid container item xs={12}>
                <Grid container item xs={8} spacing={3}>
                  <Grid item xs={6}>
                    <Controller
                      name={`publishDate`}
                      control={control}
                      defaultValue={defaultFormValues.publishDate}
                      render={({
                        field: { value, onChange, ...rest },
                        fieldState: { error },
                      }) => (
                        <KeyboardDateTimePicker
                          fullWidth
                          minutesStep={5}
                          disabled={disabled}
                          variant="inline"
                          format="yyyy-MM-dd HH:mm:00"
                          margin="dense"
                          id="date-picker-inline"
                          label="Date time"
                          {...rest}
                          value={value}
                          onChange={(date, value) => {
                            onChange(value);
                          }}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          KeyboardButtonProps={{
                            "aria-label": "change date",
                          }}
                          helperText={
                            "Your post will be posted immediately if you don’t set a custom date and time."
                          }
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>

          {disabled || (
            <div className={classes.buttonGroup}>
              <Button
                type="button"
                style={{ marginRight: "0px", padding: "9px 16px" }}
                onClick={handleSubmit(onSubmit("PREVIEW"))}
              >
                Preview
              </Button>
              <Button
                type="button"
                variant="contained"
                disableElevation
                style={{
                  color: "white",
                  background: "#306F9D",
                  marginRight: "5px",
                  padding: "9px 16px",
                }}
                onClick={handleSubmit(onSubmit("PUBLISHED"))}
              >
                Publish
              </Button>
            </div>
          )}
        </fieldset>
      </form>
    </div>
  );
}

export default AddNewsPage;
